import type { AdditionalSubnet } from '../types/gcpProject'

const trimValue = (value: unknown): string =>
	typeof value === 'string' ? value.trim() : ''

export type GcpSubnetFormInput = {
	infrastructureType: string
	nonGkeAddSubnets?: 'yes' | 'no'
	primarySubnet?: string
	controlPlaneSubnet?: string
	secondaryPodsRange?: string
	secondaryServicesRange?: string
	primarySubnetResourceType?: string
	additionalSubnets?: AdditionalSubnet[]
}

export const normalizeAdditionalSubnets = (
	value: unknown
): AdditionalSubnet[] => {
	if (!Array.isArray(value)) {
		return []
	}

	return value
		.map((item) => {
			if (typeof item === 'string') {
				return { cidr: item.trim(), resourceType: '' }
			}
			if (item && typeof item === 'object') {
				return {
					cidr: typeof item.cidr === 'string' ? item.cidr.trim() : '',
					resourceType:
						typeof item.resourceType === 'string' ? item.resourceType : ''
				}
			}
			return { cidr: '', resourceType: '' }
		})
		.filter((item) => item.cidr !== '')
}

export const resolveGcpProjectInfrastructureType = (
	details: Record<string, unknown>
): string => {
	const explicitType = trimValue(details.infrastructureType).toUpperCase()
	if (explicitType === 'NON-GKE') {
		return 'Non-GKE'
	}
	return 'GKE'
}

export const isGkeInfrastructure = (
	details: Record<string, unknown>
): boolean => {
	const type = trimValue(details.infrastructureType).toUpperCase()
	if (!type) {
		return true
	}
	return type === 'GKE'
}

export const isGcpProjectGke = (details: Record<string, unknown>): boolean =>
	details.isGKE === true ||
	details.isGKE === 'true' ||
	isGkeInfrastructure(details)

export const isValidCidr = (value: string) => {
	const trimmed = value.trim()
	const parts = trimmed.split('/')

	if (parts.length !== 2) {
		return false
	}

	const [ipPart, prefixPart] = parts
	const octets = ipPart.split('.')

	if (octets.length !== 4) {
		return false
	}

	const prefix = Number(prefixPart)
	if (!Number.isInteger(prefix) || prefix < 0 || prefix > 32) {
		return false
	}

	return octets.every((octet) => {
		if (!/^\d+$/.test(octet)) {
			return false
		}

		const numberValue = Number(octet)
		return numberValue >= 0 && numberValue <= 255
	})
}

export const VPC_CONNECTOR_RESOURCE_TYPE = 'VPC Connector'

export const isVpcConnectorResourceType = (resourceType?: string): boolean =>
	trimValue(resourceType).toLowerCase() ===
	VPC_CONNECTOR_RESOURCE_TYPE.toLowerCase()

export const isValidVpcConnectorCidr = (cidr: string): boolean => {
	if (!isValidCidr(cidr)) {
		return false
	}

	const [ipAddress, prefix] = cidr.trim().split('/')
	if (prefix !== '28') {
		return false
	}

	const octets = ipAddress.split('.').map(Number)
	return octets[3] % 16 === 0
}

export const getSubnetCidrError = (
	cidr: string,
	resourceType: string,
	label: string
): string | undefined => {
	if (!isValidCidr(cidr)) {
		return `Enter a valid CIDR for ${label}`
	}

	if (
		isVpcConnectorResourceType(resourceType) &&
		!isValidVpcConnectorCidr(cidr)
	) {
		return 'VPC Connector subnet must use a valid IPv4 /28 network range'
	}

	return undefined
}

export const hasCompleteGkeSubnetDetails = (
	details: Record<string, unknown>
): boolean =>
	Boolean(
		isValidCidr(trimValue(details.primarySubnet)) &&
			isValidCidr(trimValue(details.controlPlaneSubnet)) &&
			isValidCidr(trimValue(details.secondaryPodsRange)) &&
			isValidCidr(trimValue(details.secondaryServicesRange))
	)

export const canApproveGcpProjectSubnets = (
	details: Record<string, unknown>
): boolean => !isGcpProjectGke(details) || hasCompleteGkeSubnetDetails(details)

export const validateSubnetDetailsForApproval = (
	input: GcpSubnetFormInput
): boolean => {
	const isGke = trimValue(input.infrastructureType).toUpperCase() === 'GKE'
	const primarySubnet = trimValue(input.primarySubnet)
	const additionalSubnets = input.additionalSubnets || []
	const hasInvalidAdditionalSubnet = additionalSubnets.some(
		(subnet, index) =>
			subnet.cidr.trim() !== '' &&
			(subnet.resourceType.trim() === '' ||
				Boolean(
					getSubnetCidrError(
						subnet.cidr,
						subnet.resourceType,
						`Additional Subnet ${index + 1}`
					)
				))
	)

	if (isGke) {
		return (
			hasCompleteGkeSubnetDetails({
				primarySubnet,
				controlPlaneSubnet: input.controlPlaneSubnet,
				secondaryPodsRange: input.secondaryPodsRange,
				secondaryServicesRange: input.secondaryServicesRange
			}) && !hasInvalidAdditionalSubnet
		)
	}

	if (input.nonGkeAddSubnets !== 'yes') {
		return true
	}

	if (
		!primarySubnet ||
		getSubnetCidrError(
			primarySubnet,
			trimValue(input.primarySubnetResourceType),
			'Primary Subnet'
		)
	) {
		return false
	}

	if (!trimValue(input.primarySubnetResourceType)) {
		return false
	}

	return !hasInvalidAdditionalSubnet
}

export const buildSubnetDetailsPayload = (
	input: GcpSubnetFormInput
): Record<string, unknown> => {
	const infrastructureType = trimValue(input.infrastructureType) || 'GKE'
	const isGke = infrastructureType.toUpperCase() === 'GKE'
	const primarySubnet = trimValue(input.primarySubnet)
	const additionalSubnets = (input.additionalSubnets || [])
		.filter((subnet) => subnet.cidr.trim() !== '')
		.map((subnet) => ({
			cidr: subnet.cidr.trim(),
			resourceType: subnet.resourceType
		}))

	if (!isGke && input.nonGkeAddSubnets !== 'yes') {
		return {
			subnetRequired: 'no',
			infrastructureType,
			primarySubnet: '',
			primarySubnetResourceType: '',
			controlPlaneSubnet: '',
			secondaryPodsRange: '',
			secondaryServicesRange: '',
			additionalSubnets: []
		}
	}

	const hasNonGkeSubnetData =
		primarySubnet !== '' || additionalSubnets.length > 0
	const nextSubnetRequired = isGke || hasNonGkeSubnetData

	return {
		subnetRequired: nextSubnetRequired ? 'yes' : 'no',
		infrastructureType,
		primarySubnet: nextSubnetRequired ? primarySubnet : '',
		primarySubnetResourceType:
			nextSubnetRequired && !isGke
				? trimValue(input.primarySubnetResourceType)
				: '',
		controlPlaneSubnet:
			nextSubnetRequired && isGke ? trimValue(input.controlPlaneSubnet) : '',
		secondaryPodsRange:
			nextSubnetRequired && isGke ? trimValue(input.secondaryPodsRange) : '',
		secondaryServicesRange:
			nextSubnetRequired && isGke
				? trimValue(input.secondaryServicesRange)
				: '',
		additionalSubnets: nextSubnetRequired ? additionalSubnets : []
	}
}

export const cidrRules = (
	label: string,
	required = true,
	resourceType = ''
) => ({
	...(required ? { required: `${label} is required` } : {}),
	validate: (value: string) => {
		if (!required && !value?.trim()) {
			return true
		}

		return getSubnetCidrError(value, resourceType, label) || true
	}
})
